import React from "react";
import { Box, Container, Stack, Typography, useTheme } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useTranslation } from "react-i18next";

type ContactItemProps = {
  icon: React.ReactNode;
  heading: React.ReactNode;
  detail?: React.ReactNode;
  dark: boolean;
};

const ContactItem = ({ icon, heading, detail, dark }: ContactItemProps) => (
  <Stack direction="row" spacing={{ xs: 2, md: 2.5 }} alignItems="center">
    <Box
      aria-hidden="true"
      sx={{
        width: { xs: 42, md: 48 },
        height: { xs: 42, md: 48 },
        flex: { xs: "0 0 42px", md: "0 0 48px" },
        display: "grid",
        placeItems: "center",
        color: dark ? "#b8b8b8" : "#1A5F20",
        border: `1.5px solid ${dark ? "#a8a8a8" : "#1A5F20"}`,
        borderRadius: "50%",
        backgroundColor: dark
          ? "rgba(184,184,184,.06)"
          : "rgba(26,95,32,.04)",
        "& svg": { fontSize: { xs: 24, md: 27 } },
      }}
    >
      {icon}
    </Box>

    <Box sx={{ minWidth: 0, pt: 0.15 }}>
      <Typography
        component="div"
        sx={{
          color: dark ? "#f3f5f4" : "#101813",
          fontSize: {
            xs: `calc(15px * var(--dynamic-font-size, 1))`,
            md: `calc(18px * var(--dynamic-font-size, 1))`,
          },
          fontWeight: 700,
          lineHeight: 1.4,
          overflowWrap: "anywhere",
          "& a": {
            color: "inherit",
            textDecoration: "none",
            "&:hover, &:focus-visible": {
              color: dark ? "#fff" : "#1A5F20",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            },
          },
        }}
      >
        {heading}
      </Typography>
      {detail && (
        <Typography
          sx={{
            mt: 0.25,
            color: dark ? "#c7cdca" : "#26342b",
            fontSize: {
              xs: `calc(13px * var(--dynamic-font-size, 1))`,
              md: `calc(15px * var(--dynamic-font-size, 1))`,
            },
            lineHeight: 1.5,
          }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  </Stack>
);

const ContactUs: React.FC = () => {
  const { t } = useTranslation("home");
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const phone = t("contactUs.phone.number");
  const email = t("contactUs.email.address");

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        pt: "45px",
        pb: { xs: 7, md: 10 },
        background: "transparent",
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "100%", sm: "94%", md: "88%" } }}
      >
        <Typography
          component="h1"
          sx={{
            mb: { xs: 4, md: 5 },
            pl: { md: 1 },
            color: dark ? "#f5f7f6" : "#0f202b",
            fontSize: {
              xs: `calc(34px * var(--dynamic-font-size, 1))`,
              md: `calc(48px * var(--dynamic-font-size, 1))`,
            },
            fontWeight: 700,
            lineHeight: 1.15,
          }}
        >
          {t("contactUs.title")}
        </Typography>

        <Box
          component="section"
          aria-labelledby="contact-information-title"
          sx={{
            minHeight: { md: 480 },
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 4, md: 5.5 },
            border: `1.5px solid ${dark ? "#5f6864" : "#61dfd1"}`,
            borderRadius: { xs: "30px", md: "62px" },
            background: dark
              ? "linear-gradient(135deg, rgba(42,46,49,.97), rgba(33,43,39,.95))"
              : "linear-gradient(135deg, rgba(255,255,255,.95), rgba(235,248,255,.9) 48%, rgba(240,255,231,.92))",
          }}
        >
          <Typography
            id="contact-information-title"
            component="h2"
            sx={{
              mb: 1,
              color: dark ? "#f5f7f6" : "#0f1713",
              fontSize: {
                xs: `calc(24px * var(--dynamic-font-size, 1))`,
                md: `calc(32px * var(--dynamic-font-size, 1))`,
              },
              fontWeight: 700,
            }}
          >
            {t("contactUs.contactInfo.title")}
          </Typography>

          <Typography
            sx={{
              mb: { xs: 4, md: 5 },
              color: dark ? "#c7cdca" : "#26342b",
              fontSize: {
                xs: `calc(15px * var(--dynamic-font-size, 1))`,
                md: `calc(18px * var(--dynamic-font-size, 1))`,
              },
              lineHeight: 1.55,
            }}
          >
            {t("contactUs.contactInfo.description")}
          </Typography>

          <Stack spacing={{ xs: 4, md: 5 }}>
            <ContactItem
              dark={dark}
              icon={<PhoneIcon />}
              heading={<a href={`tel:${phone.replace(/\D/g, "")}`}>{phone}</a>}
              detail={t("contactUs.phone.availability")}
            />
            <ContactItem
              dark={dark}
              icon={<EmailIcon />}
              heading={<a href={`mailto:${email}`}>{email}</a>}
              detail={t("contactUs.email.response")}
            />
            <ContactItem
              dark={dark}
              icon={<LocationOnIcon />}
              heading={
                <>
                  {t("contactUs.address.line1")},{" "}
                  {t("contactUs.address.line2")}{" "}
                  {t("contactUs.address.line3")}
                </>
              }
            />
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactUs;
